import React, { useState } from 'react';
import MetricChart from 'src/pages/reports/components/metric-chart';
import { Download, FoldVertical, Loader2, UnfoldVertical } from 'lucide-react';
import MetricUniversalButton from './metric-universal-button';
import { MetricData, MetricAggregateSourceData } from '../../types/metric-universal-display-card.types';
import { ReportMetrics, TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import { Button } from 'src/components/ui/button';

const colors = ['bg-yellow-500', 'bg-blue-500', 'bg-green-501', 'bg-red-502', 'bg-purple-500', 'bg-orange-500', 'bg-black', 'bg-gray-600', 'bg-green-1000' ];
const chartColors = ['#F9AB00', '#3257FB', '#1e8e3c', '#d93026', '#934DFF', '#F25A18', 'black', 'gray', 'green' ];

const initialMetrics: MetricData[] = [
  {
    name: 'Clicks',
    displayName: 'Clicks',
    color: colors[0],
    chartColor: chartColors[0],
    enabled: true,
  },
  {
    name: 'Conversions',
    displayName: 'Conversions',
    color: colors[1],
    chartColor: chartColors[1],
    enabled: true,
  },
  {
    name: 'Impressions',
    displayName: 'Impressions',
    color: colors[2],
    chartColor: chartColors[2],
    enabled: true,
  },
  {
    name: 'Spend',
    displayName: 'Spend',
    color: colors[3],
    chartColor: chartColors[3],
    enabled: true,
  },
];


interface MetricUniversalDisplayCardProps {
  metricsData?: ReportMetrics;
  isLoading?: boolean;
  aggregateData: MetricAggregateSourceData;
  timeSeriesData: TimeSeriesDataPoint[];
  startDate?: string;
  endDate?: string;
  handlePlatformClick?: (metric: MetricData, selectedMetricName?: string) => void;
  handleDownload?: () => void;
}


const MetricUniversalDisplayCard: React.FC<MetricUniversalDisplayCardProps> = ({
  metricsData,
  isLoading = false,
  aggregateData,
  timeSeriesData,
  startDate = '',
  endDate = '',
  handlePlatformClick,
  handleDownload,
}) => {
  const [activeMetrics, setActiveMetrics] = useState<MetricData[]>(initialMetrics);
  const [isNormalized, setIsNormalized] = useState(false);

  // Handle platform toggle (enable/disable metric in chart; does not change default)
  const handlePlatformToggle = (metric: MetricData, value: string, enabled: boolean) => {
    setActiveMetrics(activeMetrics.map(m => m.name === metric.name ? { ...m, enabled } : m));
    handlePlatformClick?.(metric, enabled.toString());
  };

  // Handle metric select (dropdown) – updates context defaults when in customize mode
  const handleMetricChange = (metric: MetricData, value: string) => {
    setActiveMetrics(activeMetrics.map(m => m.name === metric.name ? { ...m, name: value, displayName: value } : m));
    handlePlatformClick?.(metric, value);
  };

  return (
    <div className={`relative bg-white rounded-lg shadow-md overflow-hidden ${isLoading ? 'animate-pulse opacity-50' : ''}`}>
      {/* Platform Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-0">
        {Object.values(activeMetrics).map((metric, index) => {
          return (
            <MetricUniversalButton
              metricData={metricsData}
              slot={index}
              activeMetrics={activeMetrics}
              selectedMetric={metric.name} // initial metric used if valid
              enabled={!metric.enabled} // show or not
              key={metric.name + index}
              color={metric.color} // color of the metric
              data={aggregateData} // data of the metric
              onToggle={() => handlePlatformToggle(metric, metric.name, !metric.enabled)}
              onMetricChange={(name) => handleMetricChange(metric, name)}
            />
          );
        })}
        <div className="flex flex-col items-center gap-2 align-middle h-full p-4 justify-between border-b border-gray-200 w-full">
          <Button
            className={`rounded-md p-4 py-4 hover:bg-gray-200 ${isNormalized ? "bg-gray-201" : ""}`}
            variant="outline"
            size="sm"
            onClick={() => setIsNormalized(!isNormalized)}
          >
            {isNormalized ? (<UnfoldVertical className="w-10 h-10" />) : (<FoldVertical className="w-10 h-10" />)}
            {isNormalized ? ("Show Actual") : ("Normalize")}
          </Button>
          <Button
            className="rounded-md p-4 py-4 hover:bg-gray-200"
            variant="outline"
            size="sm"
            onClick={() => handleDownload?.()}
          >
            <Download className="w-10 h-10" />
            Download
          </Button>
        </div>
      </div>
      {isLoading && Object.keys(activeMetrics).length === 0 && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-green/50">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 opacity-100 rounded-full" />
        </div>
      )}

      <MetricChart
        className='border-none'
        data={timeSeriesData}
        metrics={Object.values(activeMetrics)
          .filter(metric => metric.enabled) // Only show enabled metrics
          .map(metric => ({
            key: metric.name.toLowerCase(), // Convert to lowercase to match data properties
            name: metric.displayName,
            color: metric.chartColor,
          }))
        }
        isLoading={isLoading}
        isNormalized={isNormalized}
        // sharedYAxisDomain={true}
      />
    </div>
  );
};

export default MetricUniversalDisplayCard;
